/**
 * newsletterService, single opt-in subscription flow
 *
 * Surfaces (all use the same `<NewsletterEditorial />` UI):
 *   - inline-card   → /the-edit homepage
 *   - article-foot  → bottom of every editorial article
 *   - footer-link   → opens the homepage section via in-page anchor
 *
 * Single opt-in by design, the goal is momentum, not GDPR purity.
 * Dedup is silent: re-submitting an existing email returns success
 * without creating a duplicate row or re-sending the welcome email.
 *
 * Anti-abuse:
 *   - Honeypot field on the form (`website`, bots fill it, humans don't)
 *   - Email format check
 *   - Lower-case + trim in DB trigger
 *   - Per-IP / per-email rate limit handled at the edge function tier
 */

import { supabase } from '../lib/supabaseClient';

function isSupabaseAvailable() {
  return !!supabase;
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Subscribe an email to the newsletter.
 *
 * @param {Object} input
 *   - email     : string  (required, validated, lower-cased server-side)
 *   - name      : string? (optional)
 *   - source    : string  ('inline-card' | 'article-foot' | 'footer-link' | 'admin')
 *   - honeypot  : string? (must be empty, anti-bot)
 *
 * Returns: { ok: true, alreadySubscribed: boolean } on success
 *          { ok: false, error: string } on failure
 */
export async function subscribeNewsletter(input = {}) {
  const { email, name, source = 'inline-card', honeypot = '' } = input;

  // Anti-bot, bots fill hidden fields; if anything's in honeypot, drop it
  // silently without telling the bot anything went wrong.
  if (honeypot && honeypot.trim().length > 0) {
    return { ok: true, alreadySubscribed: true }; // pretend success
  }

  // Format validation
  if (!email || typeof email !== 'string') {
    return { ok: false, error: 'Email is required.' };
  }
  const cleanEmail = email.trim().toLowerCase();
  if (!EMAIL_RX.test(cleanEmail)) {
    return { ok: false, error: 'That doesn’t look like a valid email.' };
  }

  if (!isSupabaseAvailable()) {
    return { ok: false, error: 'Subscription service unavailable.' };
  }

  try {
    // Dedup-aware insert/reactivate + returning the row, all done inside
    // a SECURITY DEFINER RPC: anon has no SELECT on this table (subscriber
    // data is private), so a plain insert().select() gets 0 rows back from
    // PostgREST and throws even though the insert itself succeeded.
    const { data: rows, error: rpcError } = await supabase.rpc('subscribe_to_newsletter', {
      p_email: cleanEmail,
      p_name: name?.trim() || null,
      p_source: source,
    });

    if (rpcError) throw rpcError;
    const inserted = Array.isArray(rows) ? rows[0] : rows;
    if (!inserted) throw new Error('subscribe_to_newsletter returned no row');

    if (inserted.already_subscribed) {
      return { ok: true, alreadySubscribed: true };
    }

    // Fire welcome email, non-blocking. If it fails, the subscription
    // still succeeded; we log and move on so the user always sees the
    // success state.
    sendWelcomeEmail(inserted).catch(err => {
      console.warn('[newsletterService] welcome email failed:', err);
    });

    return { ok: true, alreadySubscribed: false };
  } catch (err) {
    console.error('[newsletterService] subscribe failed:', err);
    return { ok: false, error: 'Could not save your subscription. Please try once more.' };
  }
}

/**
 * Unsubscribe via the token embedded in every welcome / weekly email.
 * Idempotent, calling twice is fine.
 */
export async function unsubscribeNewsletter(token) {
  if (!isSupabaseAvailable() || !token) {
    return { ok: false, error: 'Invalid unsubscribe link.' };
  }
  try {
    const { error } = await supabase
      .from('newsletter_subscribers')
      .update({ status: 'unsubscribed', unsubscribed_at: new Date().toISOString() })
      .eq('unsubscribe_token', token);
    if (error) throw error;
    return { ok: true };
  } catch (err) {
    console.error('[newsletterService] unsubscribe failed:', err);
    return { ok: false, error: 'Could not unsubscribe. Please contact us.' };
  }
}

/**
 * Welcome email via the existing `send-email` edge function (Resend).
 * Plain editorial note, no images, one CTA-shaped link, restrained.
 */
async function sendWelcomeEmail(subscriber) {
  if (!isSupabaseAvailable()) return;

  const greeting = subscriber.name
    ? `Dear ${subscriber.name.split(' ')[0]},`
    : 'Dear Reader,';

  const unsubscribeUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://luxuryweddingdirectory.com'}/unsubscribe?token=${subscriber.unsubscribe_token}`;

  const html = buildWelcomeEmail({ greeting, unsubscribeUrl });

  // Calls the existing send-email edge function (same one used by
  // ListYourBusinessPage's confirmation flow). Falls back gracefully
  // if the function isn't deployed in this environment.
  try {
    await supabase.functions.invoke('send-email', {
      body: {
        to: subscriber.email,
        subject: 'Welcome, your first letter arrives Sunday',
        html,
      },
    });
  } catch (err) {
    console.warn('[newsletterService] send-email function unavailable:', err);
  }
}

function buildWelcomeEmail({ greeting, unsubscribeUrl }) {
  // Plain, editorial-voice HTML. No background images, no buttons.
  // One gold rule, one farewell, one tiny unsubscribe link.
  return `<!doctype html>
<html><head><meta charset="utf-8"/></head>
<body style="margin:0;padding:48px 24px;background:#f7f4ef;font-family:'Jost',Helvetica,Arial,sans-serif;color:#181410;line-height:1.65;">
  <table cellpadding="0" cellspacing="0" border="0" style="max-width:540px;margin:0 auto;background:#ffffff;padding:56px 40px;">
    <tr><td>
      <div style="font-size:9px;font-weight:600;letter-spacing:0.4em;text-transform:uppercase;color:#b29565;margin-bottom:24px;">
        Luxury Wedding Directory
      </div>

      <p style="margin:0 0 20px;font-size:15px;">${greeting}</p>

      <p style="margin:0 0 20px;font-size:15px;">
        Thank you for joining <em>The LWD Edit</em>. Your first letter arrives this Sunday.
      </p>

      <p style="margin:0 0 8px;font-size:15px;">What you'll find inside, week by week:</p>

      <ul style="margin:0 0 24px;padding-left:18px;font-size:14px;">
        <li style="margin-bottom:6px;">One cover story before it goes live</li>
        <li style="margin-bottom:6px;">An editor-vetted address, with our notes</li>
        <li style="margin-bottom:6px;">A short list of what we found worth a journey</li>
      </ul>

      <p style="margin:0 0 28px;font-size:15px;">Until Sunday,</p>

      <p style="margin:0;font-style:italic;font-family:'Cormorant Garamond',Georgia,serif;font-size:18px;color:#181410;">
        The Editorial Desk
      </p>

      <hr style="margin:40px 0 20px;border:none;border-top:1px solid rgba(178,149,101,0.3);" />

      <p style="margin:0;font-size:11px;color:rgba(24,20,16,0.5);font-style:italic;">
        You can <a href="${unsubscribeUrl}" style="color:rgba(24,20,16,0.5);text-decoration:underline;">unsubscribe</a> at any time.
      </p>
    </td></tr>
  </table>
</body></html>`;
}

/**
 * Admin: list subscribers (most-recent first) + counts.
 */
export async function listNewsletterSubscribers({ limit = 200, offset = 0, status } = {}) {
  if (!isSupabaseAvailable()) return { data: [], counts: {}, error: null };
  try {
    let q = supabase
      .from('newsletter_subscribers')
      .select('id, email, name, status, source, subscribed_at, unsubscribed_at', { count: 'exact' })
      .order('subscribed_at', { ascending: false })
      .range(offset, offset + limit - 1);
    if (status) q = q.eq('status', status);
    const { data, error, count } = await q;
    if (error) throw error;

    // Status counts (cheap second query)
    const { data: countData } = await supabase
      .from('newsletter_subscribers')
      .select('status');
    const counts = {
      active:       (countData || []).filter(r => r.status === 'active').length,
      unsubscribed: (countData || []).filter(r => r.status === 'unsubscribed').length,
      total:        (countData || []).length,
    };

    return { data: data || [], counts, totalMatching: count, error: null };
  } catch (err) {
    console.error('[newsletterService] listSubscribers failed:', err);
    return { data: [], counts: {}, error: err };
  }
}

/**
 * Admin: export all subscribers as CSV string (download in browser).
 */
export async function exportNewsletterCSV() {
  if (!isSupabaseAvailable()) return '';
  const { data } = await supabase
    .from('newsletter_subscribers')
    .select('email, name, status, source, subscribed_at, unsubscribed_at')
    .order('subscribed_at', { ascending: false });

  const rows = data || [];
  const header = ['email', 'name', 'status', 'source', 'subscribed_at', 'unsubscribed_at'];
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    header.join(','),
    ...rows.map(r => header.map(k => escape(r[k])).join(',')),
  ];
  return lines.join('\n');
}
